// NotesApp source file: note-actions.service.ts

import { Injectable, Injector, inject } from '@angular/core';
import { ConfirmationService } from 'primeng/api';

import { DataService, Note } from './data.service';
import { NotePrintExportService } from './note-print-export.service';
import { SnackbarService } from './snackbar.service';
import { getNoteDisplayTitle } from '../_util/note-display-title.util';
import { TRASH_RETENTION_DAYS } from '../_util/trash.util';
import type {
  BulkActionConfig,
  BulkActionParams,
} from '../_components/home/bulk-action-dialog/bulk-action-dialog.component';

/** Single-note export formats. `docx` needs the options dialog first (see DOCX_EXPORT_ACTION). */
export type NoteExportFormat = 'pdf' | 'pdf-images' | 'docx' | 'json';

/**
 * Single-note actions shared by the View page menu and the Home list's long-press menu,
 * so both run the exact same export and delete logic (and show the same messages/confirmation).
 */
@Injectable({ providedIn: 'root' })
export class NoteActionsService {
  // Resolved on first use, not at construction: View and the Home list inject this service just to
  // have it at hand, and most of them never export or delete anything.
  private readonly injector = inject(Injector);
  private get dataService(): DataService {
    return this.injector.get(DataService);
  }
  private get printExport(): NotePrintExportService {
    return this.injector.get(NotePrintExportService);
  }
  private get snackbar(): SnackbarService {
    return this.injector.get(SnackbarService);
  }

  /** Config for <app-bulk-action-dialog> when it collects the DOCX options for ONE note. */
  static readonly DOCX_EXPORT_ACTION: BulkActionConfig = {
    id: 'export-docx',
    label: 'Export DOCX',
    description: 'Configure DOCX export options for this note.',
    requiresParams: true,
  };

  /** PDF (with or without images). */
  async exportToPdf(note: Note | undefined, options?: { includeAttachments?: boolean }): Promise<void> {
    await this.printOrExport(note, 'export', options);
  }

  async printOrExport(
    note: Note | undefined,
    mode: 'print' | 'export',
    options?: { includeAttachments?: boolean },
  ): Promise<void> {
    if (!note) {
      this.snackbar.showErrorMessage('Note is not available for export.');
      return;
    }
    try {
      await this.printExport.printOrExportNote(note, mode, options);
    } catch (error) {
      console.error('Failed to generate PDF', error);
      this.snackbar.showErrorMessage('Unable to generate PDF. Please try again.');
    }
  }

  /** DOCX, with the options collected by the DOCX dialog. */
  async exportToDocx(note: Note | undefined, params?: BulkActionParams): Promise<void> {
    if (!note?._id) {
      this.snackbar.showErrorMessage('Note is not available for export.');
      return;
    }
    try {
      await this.printExport.exportNotesToDocxByIds([note._id], [note], {
        fileName: params?.fileName,
        includeTableOfContents: params?.includeTableOfContents,
        pageBreakBetweenNotes: false,
        pageNumbers: params?.pageNumbers,
        includeMetaData: params?.includeMetaData,
      });
      this.snackbar.showSuccessMessage('Exported note to DOCX.');
    } catch (error) {
      console.error('Failed to generate DOCX', error);
      this.snackbar.showErrorMessage('Unable to generate DOCX. Please try again.');
    }
  }

  async exportToJson(note: Note | undefined): Promise<void> {
    if (!note?._id) {
      this.snackbar.showErrorMessage('Note is not available for export.');
      return;
    }
    try {
      await this.printExport.exportNotesToJson([note]);
      this.snackbar.showSuccessMessage('Exported note to JSON.');
    } catch (error) {
      console.error('Failed to generate JSON export', error);
      this.snackbar.showErrorMessage('Unable to generate JSON export. Please try again.');
    }
  }

  /**
   * Delete = Move to Trash (tasks/delete_trash_client.md): asks for confirmation, then moves the note
   * to Trash through DataService.deleteNote() (offline-first). `onDeleted` runs once the note is gone
   * from this device's normal views — also when the server is only updated on the next sync.
   * Pass the caller's own ConfirmationService when it has a component-level one (with its own
   * <p-confirmDialog>); otherwise the app-wide instance (rendered in AppComponent) is used.
   */
  confirmAndDelete(
    note: Note | undefined,
    onDeleted: () => void,
    confirmation: ConfirmationService = this.injector.get(ConfirmationService),
  ): void {
    if (!note) return;

    confirmation.confirm({
      header: 'Move to Trash',
      message:
        `Move "${getNoteDisplayTitle(note.Title, note.DescriptionHtml)}" to Trash? ` +
        `You can restore it from Trash for ${TRASH_RETENTION_DAYS} days.`,
      icon: 'pi pi-trash',
      acceptLabel: 'Move to Trash',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.moveToTrash(note, onDeleted),
    });
  }

  /** The move itself (no confirmation) with the standard toasts. */
  moveToTrash(note: Note, onDeleted: () => void = () => {}): void {
    this.dataService.deleteNote(note).subscribe({
      next: (response) => {
        if (response?.status === 'error') {
          this.snackbar.showErrorMessage('The note could not be moved to Trash: ' + (response.error?.message ?? 'unknown error'));
          return;
        }
        this.snackbar.showSuccessMessage(
          response?.status === 'warning' ? 'Note moved to Trash. It will sync when you are back online.' : 'Note moved to Trash',
        );
        // Refresh template cache in case a template was deleted
        this.dataService.refreshTemplateCache();
        onDeleted();
      },
      error: (error) => {
        console.error('Move to Trash failed:', error);
        this.snackbar.showErrorMessage('The note could not be moved to Trash: ' + (error?.error?.message ?? error?.message ?? error));
      },
    });
  }
}
